import { c as _c } from "react/compiler-runtime";
import type { Factiii } from '@shared/all/domains/factiiis';
import type { Post, PostResponse } from '@shared/all/domains/posts';
import { type InfiniteData, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import useSocket from './useSocket';
import useUser from './useUser';
type Options = {
  spaceId: number | undefined;
  spaceSlug: string;
  isBoardLayout: boolean;
  onSpaceDeleted: () => void;
};
const useBoardRealtime = t0 => {
  const $ = _c(28);
  const {
    spaceId,
    spaceSlug,
    isBoardLayout,
    onSpaceDeleted
  } = t0;
  const queryClient = useQueryClient();
  const {
    currentUser
  } = useUser();
  const currentUsername = currentUser.username;
  const {
    joinBoardRoom,
    leaveBoardRoom,
    onConnect,
    onSpaceMeta,
    onSpaceRules,
    onSpaceMembers,
    onSpaceDeleted: subscribeSpaceDeleted,
    onBoardUpdate,
    onPostCreated,
    onPostMoved,
    onPostReordered,
    onPostEdited,
    onPostVoted,
    onPostDeleted,
    onFactiiiReordered
  } = useSocket();
  let t1;
  if ($[0] !== currentUsername || $[1] !== isBoardLayout || $[2] !== joinBoardRoom || $[3] !== leaveBoardRoom || $[4] !== onBoardUpdate || $[5] !== onConnect || $[6] !== onFactiiiReordered || $[7] !== onPostCreated || $[8] !== onPostDeleted || $[9] !== onPostEdited || $[10] !== onPostMoved || $[11] !== onPostReordered || $[12] !== onPostVoted || $[13] !== onSpaceDeleted || $[14] !== onSpaceMembers || $[15] !== onSpaceMeta || $[16] !== onSpaceRules || $[17] !== queryClient || $[18] !== spaceId || $[19] !== spaceSlug || $[20] !== subscribeSpaceDeleted) {
    t1 = () => {
      if (!spaceId || !spaceSlug || !isBoardLayout) {
        return;
      }
      const spaceKey = ["space", spaceSlug, currentUsername];
      const invalidateSpace = () => void queryClient.invalidateQueries({
        queryKey: spaceKey
      });
      const patchFactiiiPosts = (factiiiId, mutate) => {
        queryClient.setQueriesData({
          predicate: q => {
            const k = q.queryKey;
            return Array.isArray(k) && k[k.length - 1] === `${factiiiId}-${spaceSlug}`;
          }
        }, old => {
          if (!old) {
            return old;
          }
          const all = old.pages.flatMap(_temp);
          return {
            ...old,
            pages: [{
              posts: mutate(all)
            }]
          };
        });
      };
      const patchSpacePosts = mutate_0 => {
        queryClient.setQueriesData({
          predicate: q_0 => {
            const k_0 = q_0.queryKey;
            return Array.isArray(k_0) && typeof k_0[k_0.length - 1] === "string" && (k_0[k_0.length - 1] as string).endsWith(`-${spaceSlug}`);
          }
        }, old_0 => {
          if (!old_0) {
            return old_0;
          }
          const all_0 = old_0.pages.flatMap(_temp2);
          return {
            ...old_0,
            pages: [{
              posts: mutate_0(all_0)
            }]
          };
        });
      };
      const patchBoardFactiiis = mutate_1 => {
        queryClient.setQueryData(["boardFactiiis", spaceSlug], old_1 => {
          if (!old_1) {
            return old_1;
          }
          const all_1 = mutate_1(old_1.pages.flatMap(_temp3));
          return {
            ...old_1,
            pages: [{
              factiiis: all_1
            }]
          };
        });
      };
      joinBoardRoom(spaceId);
      const cleanups = [onConnect(() => joinBoardRoom(spaceId)), onSpaceMeta(invalidateSpace), onSpaceRules(invalidateSpace), onSpaceMembers(() => {
        invalidateSpace();
        queryClient.invalidateQueries({
          queryKey: ["SpaceMembersList", spaceSlug]
        });
        queryClient.invalidateQueries({
          queryKey: ["users", "space_members"]
        });
        queryClient.invalidateQueries({
          queryKey: ["moderatorsList", spaceSlug]
        });
        queryClient.invalidateQueries({
          queryKey: ["banned_users", spaceSlug]
        });
        queryClient.invalidateQueries({
          queryKey: ["space-membership", spaceSlug]
        });
      }), subscribeSpaceDeleted(onSpaceDeleted), onBoardUpdate(scope => {
        if (scope === "factiiis" || scope === "all") {
          queryClient.invalidateQueries({
            queryKey: ["boardFactiiis", spaceSlug]
          });
        }
        if (scope === "posts" || scope === "all") {
          queryClient.invalidateQueries({
            predicate: q_1 => {
              const k_1 = q_1.queryKey;
              return Array.isArray(k_1) && typeof k_1[k_1.length - 1] === "string" && (k_1[k_1.length - 1] as string).endsWith(`-${spaceSlug}`);
            }
          });
        }
        queryClient.invalidateQueries({
          queryKey: ["boardActivity", spaceSlug]
        });
        queryClient.invalidateQueries({
          predicate: _temp4
        });
      }), onPostCreated((post, factiiiId_0) => {
        patchFactiiiPosts(factiiiId_0, posts => posts.some(p_2 => p_2.id === post.id) ? posts : [...posts, post]);
        queryClient.invalidateQueries({
          queryKey: ["boardActivity", spaceSlug]
        });
      }), onPostMoved((post_0, fromFactiiiId, toFactiiiId) => {
        patchFactiiiPosts(fromFactiiiId, posts_0 => posts_0.filter(p_3 => p_3.id !== post_0.id));
        patchFactiiiPosts(toFactiiiId, posts_1 => {
          const without = posts_1.filter(p_4 => p_4.id !== post_0.id);
          return [...without, post_0].sort(_temp5);
        });
        queryClient.invalidateQueries({
          queryKey: ["boardActivity", spaceSlug]
        });
      }), onPostReordered((postId, factiiiId_1, pos) => {
        patchFactiiiPosts(factiiiId_1, posts_2 => posts_2.map(p_5 => p_5.id === postId ? {
          ...p_5,
          pos
        } : p_5).sort(_temp6));
      }), onFactiiiReordered((factiiiId_2, pos_0) => {
        patchBoardFactiiis(factiiis => factiiis.map(f => f.id === factiiiId_2 ? {
          ...f,
          pos: pos_0
        } : f).sort(_temp7));
      }), onPostEdited(post_1 => {
        patchSpacePosts(posts_3 => posts_3.map(p_6 => p_6.id === post_1.id ? post_1 : p_6));
        queryClient.invalidateQueries({
          queryKey: ["boardActivity", spaceSlug]
        });
      }), onPostVoted((postId_0, count) => {
        patchSpacePosts(posts_4 => posts_4.map(p_7 => p_7.id === postId_0 ? {
          ...p_7,
          voteCount: count
        } : p_7));
      }), onPostDeleted(postId_1 => {
        patchSpacePosts(posts_5 => posts_5.filter(p_8 => p_8.id !== postId_1));
        queryClient.invalidateQueries({
          queryKey: ["boardActivity", spaceSlug]
        });
      })];
      return () => {
        leaveBoardRoom(spaceId);
        cleanups.forEach(_temp8);
      };
    };
    $[0] = currentUsername;
    $[1] = isBoardLayout;
    $[2] = joinBoardRoom;
    $[3] = leaveBoardRoom;
    $[4] = onBoardUpdate;
    $[5] = onConnect;
    $[6] = onFactiiiReordered;
    $[7] = onPostCreated;
    $[8] = onPostDeleted;
    $[9] = onPostEdited;
    $[10] = onPostMoved;
    $[11] = onPostReordered;
    $[12] = onPostVoted;
    $[13] = onSpaceDeleted;
    $[14] = onSpaceMembers;
    $[15] = onSpaceMeta;
    $[16] = onSpaceRules;
    $[17] = queryClient;
    $[18] = spaceId;
    $[19] = spaceSlug;
    $[20] = subscribeSpaceDeleted;
    $[21] = t1;
  } else {
    t1 = $[21];
  }
  let t2;
  if ($[22] !== currentUsername || $[23] !== isBoardLayout || $[24] !== queryClient || $[25] !== spaceId || $[26] !== spaceSlug) {
    t2 = [spaceId, spaceSlug, currentUsername, isBoardLayout, queryClient];
    $[22] = currentUsername;
    $[23] = isBoardLayout;
    $[24] = queryClient;
    $[25] = spaceId;
    $[26] = spaceSlug;
    $[27] = t2;
  } else {
    t2 = $[27];
  }
  useEffect(t1, t2);
};
export default useBoardRealtime;
function _temp(p) {
  return p.posts;
}
function _temp2(p_0) {
  return p_0.posts;
}
function _temp3(p_1) {
  return p_1.factiiis;
}
function _temp4(q_2) {
  return Array.isArray(q_2.queryKey) && q_2.queryKey[0] === "cardActivity";
}
function _temp5(a, b) {
  return (a.pos ?? 0) - (b.pos ?? 0);
}
function _temp6(a_0, b_0) {
  return (a_0.pos ?? 0) - (b_0.pos ?? 0);
}
function _temp7(a_1, b_1) {
  return (a_1.pos ?? 0) - (b_1.pos ?? 0);
}
function _temp8(cleanup) {
  return cleanup();
}